import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/auth';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { Delete, Search, CheckCircle, Undo, SaveAlt } from '@mui/icons-material';
import { getClientes } from '../../api/clientes';
import { getServiciosParaFacturar, marcarFacturados } from '../../api/facturacion';
import { ConfirmDelete } from '../../components/ConfirmDelete';
import { UseAlert } from '../../components/newAlert/NewAlert';
import moment from 'moment';
import XLSX from 'xlsx-js-style';

interface Registro {
  SrvD_Id: number;
  SrvD_ValorUnitario: string;
  SrvD_Iva: string;
  SrvD_Total: string;
  SrvD_Cantidad?: number;
  item_facturacion: string | null;
  Pr_Nombre: string;
  Srv_FechaServicio: string;
  Srv_Factura: string;
  Srv_Id: number;
}

interface ServicioAgrupado {
  Srv_Id: number;
  Srv_VehiculoMarca: string;
  Srv_VehiculoReferencia: string;
  Srv_VehiculoPlacaOSerial: string;
  registros: Registro[];
}

interface Cliente {
  Cl_Id: number;
  Cl_Nombre: string;
}

const Facturacion = () => {
  const [servicios, setServicios] = useState<ServicioAgrupado[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [showCantidad, setShowCantidad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [searched, setSearched] = useState(false);

  // Track removed detail IDs locally
  const [removedDetailIds, setRemovedDetailIds] = useState<Set<number>>(new Set());

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteDetailId, setDeleteDetailId] = useState<number | null>(null);

  const [loadingFacturar, setLoadingFacturar] = useState(false);

  // Dialog para desmarcar
  const [openDesmarcarDialog, setOpenDesmarcarDialog] = useState(false);
  const [desmarcarIdsText, setDesmarcarIdsText] = useState('');
  const [loadingDesmarcar, setLoadingDesmarcar] = useState(false);

  // Editable item_facturacion por SrvD_Id
  const [itemFacturacionMap, setItemFacturacionMap] = useState<Record<number, string>>({});

  const { permisos } = useAuth();
  const { Prm_Fact_Modificar } = permisos || {};
  const { NewNotification } = UseAlert();

  const fetchClientes = useCallback(async () => {
    setLoadingClientes(true);
    const response = await getClientes();
    if (response.isOk) {
      setClientes(response.data);
    }
    setLoadingClientes(false);
  }, []);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleBuscar = useCallback(async () => {
    if (!selectedCliente) {
      NewNotification('Seleccione un cliente', 'warning');
      return;
    }
    if (!fechaInicio || !fechaFin) {
      NewNotification('Seleccione un rango de fechas', 'warning');
      return;
    }

    setLoading(true);
    setSearched(true);
    setRemovedDetailIds(new Set());

    const response = await getServiciosParaFacturar({
      cliente_id: selectedCliente.Cl_Id,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      show_cantidad: showCantidad || undefined,
    });

    if (response.isOk) {
      setServicios(response.data || []);
    } else {
      NewNotification(response.message || 'Error al consultar', 'error');
      setServicios([]);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente, fechaInicio, fechaFin, showCantidad]);

  // Filter out removed details
  const filteredServicios = servicios
    .map((srv) => ({
      ...srv,
      registros: srv.registros.filter((r) => !removedDetailIds.has(r.SrvD_Id)),
    }))
    .filter((srv) => srv.registros.length > 0);

  const handleOpenDeleteConfirm = useCallback((detailId: number) => {
    setDeleteDetailId(detailId);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteDetailId(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteDetailId !== null) {
      setRemovedDetailIds((prev) => new Set(prev).add(deleteDetailId));
      handleCloseDeleteConfirm();
    }
  }, [deleteDetailId, handleCloseDeleteConfirm]);

  // Get all visible Srv_Ids
  const getVisibleSrvIds = useCallback((): number[] => {
    const ids = new Set<number>();
    filteredServicios.forEach((srv) => {
      srv.registros.forEach((r) => ids.add(r.Srv_Id));
    });
    return Array.from(ids);
  }, [filteredServicios]);

  const handleMarcarFacturados = useCallback(async () => {
    const ids = getVisibleSrvIds();
    if (ids.length === 0) {
      NewNotification('No hay servicios para facturar', 'warning');
      return;
    }
    setLoadingFacturar(true);
    const response = await marcarFacturados(ids, 'facturar');
    if (response.isOk) {
      NewNotification('Servicios marcados como facturados exitosamente', 'success');
      handleBuscar();
    } else {
      NewNotification(response.message || 'Error al marcar como facturados', 'error');
    }
    setLoadingFacturar(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVisibleSrvIds, handleBuscar]);

  const handleDesmarcarFacturados = useCallback(async () => {
    const ids = desmarcarIdsText
      .split(/[,;\s]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    if (ids.length === 0) {
      NewNotification('Ingrese al menos un ID de servicio válido', 'warning');
      return;
    }
    setLoadingDesmarcar(true);
    const response = await marcarFacturados(ids, 'desmarcar');
    if (response.isOk) {
      NewNotification('Servicios desmarcados exitosamente', 'success');
      setOpenDesmarcarDialog(false);
      setDesmarcarIdsText('');
      if (searched) handleBuscar();
    } else {
      NewNotification(response.message || 'Error al desmarcar', 'error');
    }
    setLoadingDesmarcar(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desmarcarIdsText, handleBuscar, searched]);

  // Calculate totals
  const totales = filteredServicios.reduce(
    (acc, srv) => {
      srv.registros.forEach((r) => {
        acc.subtotal += Number(r.SrvD_ValorUnitario) || 0;
        acc.iva += Number(r.SrvD_Iva) || 0;
        acc.total += Number(r.SrvD_Total) || 0;
      });
      return acc;
    },
    { subtotal: 0, iva: 0, total: 0 }
  );

  const formatCurrency = (value: number | string) => {
    const num = Number(value);
    return num.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 });
  };

  const handleItemFacturacionChange = useCallback((srvdId: number, value: string) => {
    setItemFacturacionMap((prev) => ({ ...prev, [srvdId]: value }));
  }, []);

  const getItemFacturacion = (reg: Registro) => {
    return itemFacturacionMap[reg.SrvD_Id] ?? reg.item_facturacion ?? '';
  };

  // Excel export with styles
  const handleExportExcel = useCallback(() => {
    if (filteredServicios.length === 0) {
      NewNotification('No hay datos para exportar', 'warning');
      return;
    }

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '1076B5' } },
      alignment: { horizontal: 'center' as const, vertical: 'center' as const, wrapText: true },
      border: {
        top: { style: 'thin' as const, color: { rgb: '000000' } },
        bottom: { style: 'thin' as const, color: { rgb: '000000' } },
        left: { style: 'thin' as const, color: { rgb: '000000' } },
        right: { style: 'thin' as const, color: { rgb: '000000' } },
      },
    };

    const cellStyle = {
      alignment: { horizontal: 'center' as const, vertical: 'center' as const },
      border: {
        top: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
        bottom: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
        left: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
        right: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
      },
      font: { sz: 9 },
    };

    const currencyStyle = {
      ...cellStyle,
      numFmt: '$#,##0',
    };

    const totalStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: 'center' as const, vertical: 'center' as const },
      fill: { fgColor: { rgb: 'E3F2FD' } },
      border: {
        top: { style: 'medium' as const, color: { rgb: '000000' } },
        bottom: { style: 'medium' as const, color: { rgb: '000000' } },
        left: { style: 'thin' as const, color: { rgb: '000000' } },
        right: { style: 'thin' as const, color: { rgb: '000000' } },
      },
      numFmt: '$#,##0',
    };

    const titleStyle = {
      font: { bold: true, sz: 14, color: { rgb: '1076B5' } },
      alignment: { horizontal: 'center' as const, vertical: 'center' as const },
    };

    // Build headers
    const headers = ['#', 'MARCA', 'VEHÍCULO', 'CHASIS/PLACA', 'SUB TOTAL', 'IVA', 'TOTAL', 'ITEM FACTURACIÓN', 'DESCRIPCIÓN DEL SERVICIO'];
    if (showCantidad) headers.push('CANTIDAD');
    headers.push('FECHA INSTALACIÓN', 'FACTURA');

    const colCount = headers.length;

    // Title rows
    const clienteName = selectedCliente?.Cl_Nombre || 'Cliente';
    const titleText = `FACTURACIÓN - ${clienteName}`;
    const rangeText = `Período: ${moment(fechaInicio).format('DD/MM/YYYY')} al ${moment(fechaFin).format('DD/MM/YYYY')}`;

    const aoa: any[][] = [];
    // Row 0: Title
    aoa.push([titleText, ...Array(colCount - 1).fill('')]);
    // Row 1: Date range
    aoa.push([rangeText, ...Array(colCount - 1).fill('')]);
    // Row 2: Empty
    aoa.push(Array(colCount).fill(''));
    // Row 3: Headers
    aoa.push(headers);

    // Data rows
    let rowNum = 1;
    filteredServicios.forEach((srv) => {
      srv.registros.forEach((reg, idx) => {
        const row: any[] = [
          idx === 0 ? rowNum : '',
          idx === 0 ? srv.Srv_VehiculoMarca : '',
          idx === 0 ? srv.Srv_VehiculoReferencia : '',
          idx === 0 ? srv.Srv_VehiculoPlacaOSerial : '',
          Number(reg.SrvD_ValorUnitario),
          Number(reg.SrvD_Iva),
          Number(reg.SrvD_Total),
          getItemFacturacion(reg),
          reg.Pr_Nombre,
        ];
        if (showCantidad) row.push(reg.SrvD_Cantidad ?? '');
        row.push(moment(reg.Srv_FechaServicio).format('DD-MMM-YY'), reg.Srv_Factura);
        aoa.push(row);
        if (idx === 0) rowNum++;
      });
    });

    // Totals row
    const totalRow: any[] = ['', '', '', 'TOTALES', totales.subtotal, totales.iva, totales.total, '', ''];
    if (showCantidad) totalRow.push('');
    totalRow.push('', '');
    aoa.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Merge title cells
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    ];

    // Column widths
    const baseWidths = [
      { wch: 4 },   // #
      { wch: 14 },  // Marca
      { wch: 18 },  // Vehículo
      { wch: 24 },  // Chasis/Placa
      { wch: 14 },  // Sub Total
      { wch: 12 },  // IVA
      { wch: 14 },  // Total
      { wch: 16 },  // Item Facturación
      { wch: 35 },  // Descripción
    ];
    if (showCantidad) baseWidths.push({ wch: 10 });
    baseWidths.push({ wch: 16 }, { wch: 14 });
    ws['!cols'] = baseWidths;

    // Row heights
    ws['!rows'] = [
      { hpt: 28 },  // Title
      { hpt: 18 },  // Range
      { hpt: 10 },  // Empty
      { hpt: 22 },  // Header
    ];

    // Apply styles
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };

        if (R === 0) {
          ws[addr].s = titleStyle;
        } else if (R === 1) {
          ws[addr].s = { font: { sz: 10, italic: true }, alignment: { horizontal: 'center' as const } };
        } else if (R === 3) {
          ws[addr].s = headerStyle;
        } else if (R === range.e.r) {
          // Totals row
          ws[addr].s = totalStyle;
        } else if (R > 3) {
          // Data rows — currency columns
          if (C >= 4 && C <= 6) {
            ws[addr].s = currencyStyle;
          } else {
            ws[addr].s = cellStyle;
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Facturación');

    const fileName = `Facturacion_${clienteName}_${fechaInicio}_${fechaFin}.xlsx`;
    XLSX.writeFile(wb, fileName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredServicios, showCantidad, totales, selectedCliente, fechaInicio, fechaFin]);

  return (
    <div style={{ marginTop: '5px' }}>
      {/* Filters */}
      <Grid container spacing={2} alignItems='center' mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={clientes}
            getOptionLabel={(option: Cliente) => option.Cl_Nombre}
            isOptionEqualToValue={(option, value) => option.Cl_Id === value.Cl_Id}
            value={selectedCliente}
            onChange={(_, value) => setSelectedCliente(value)}
            loading={loadingClientes}
            renderInput={(params) => <TextField {...params} label='Cliente' size='small' />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label='Fecha Inicio'
            type='date'
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size='small'
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label='Fecha Fin'
            type='date'
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size='small'
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControlLabel
            control={<Checkbox checked={showCantidad} onChange={(e) => setShowCantidad(e.target.checked)} size='small' />}
            label='Mostrar Cantidad'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <IconButton onClick={handleBuscar} title='Buscar' disabled={loading}
            className='tool-btn'>
            <Search />
          </IconButton>
          {Prm_Fact_Modificar === 1 && (
            <IconButton onClick={() => setOpenDesmarcarDialog(true)} title='Desmarcar facturados'
              className='tool-btn'>
              <Undo />
            </IconButton>
          )}
          {searched && filteredServicios.length > 0 && (
            <>
              <IconButton onClick={handleExportExcel} title='Exportar Excel'
                className='tool-btn'>
                <SaveAlt />
              </IconButton>
              {Prm_Fact_Modificar === 1 && (
                <IconButton onClick={handleMarcarFacturados} title='Marcar Facturados' disabled={loadingFacturar}
                  className='tool-btn'>
                  {loadingFacturar ? <CircularProgress size={20} /> : <CheckCircle />}
                </IconButton>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Totals info */}
      {searched && filteredServicios.length > 0 && (
        <Box display='flex' gap={2} mb={1}>
          <Typography variant='body2'><strong>Servicios:</strong> {filteredServicios.length}</Typography>
          <Typography variant='body2'><strong>Total:</strong> {formatCurrency(totales.total)}</Typography>
        </Box>
      )}

      {/* Data table */}
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
          <CircularProgress />
        </Box>
      ) : searched && filteredServicios.length === 0 ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
          <Typography color='text.secondary'>No se encontraron servicios para facturar</Typography>
        </Box>
      ) : (
        filteredServicios.length > 0 && (
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(120deg, rgba(14,159,143,0.14), rgba(124,108,240,0.10))' }}>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>#</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Marca</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Vehículo</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Chasis/Placa</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Sub Total</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>IVA</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Total</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Facturación</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Descripción del Servicio</TableCell>
                  {showCantidad && (
                    <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Cantidad</TableCell>
                  )}
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Fecha Instalación</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Factura</TableCell>
                  <TableCell align='center' sx={{ color: 'primary.dark', fontWeight: 700 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServicios.map((srv, srvIdx) => (
                  <React.Fragment key={srv.Srv_Id}>
                    {srv.registros.map((reg, regIdx) => (
                      <TableRow
                        key={reg.SrvD_Id}
                        sx={{
                          backgroundColor: srvIdx % 2 === 0 ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.22)',
                          borderTop: regIdx === 0 ? '2px solid rgba(14,159,143,0.32)' : undefined,
                        }}
                      >
                        {regIdx === 0 && (
                          <>
                            <TableCell align='center' rowSpan={srv.registros.length}>
                              {srvIdx + 1}
                            </TableCell>
                            <TableCell align='center' rowSpan={srv.registros.length}>
                              {srv.Srv_VehiculoMarca}
                            </TableCell>
                            <TableCell align='center' rowSpan={srv.registros.length}>
                              {srv.Srv_VehiculoReferencia}
                            </TableCell>
                            <TableCell align='center' rowSpan={srv.registros.length}>
                              {srv.Srv_VehiculoPlacaOSerial}
                            </TableCell>
                          </>
                        )}
                        <TableCell align='center'>{formatCurrency(reg.SrvD_ValorUnitario)}</TableCell>
                        <TableCell align='center'>{formatCurrency(reg.SrvD_Iva)}</TableCell>
                        <TableCell align='center'>{formatCurrency(reg.SrvD_Total)}</TableCell>
                        <TableCell align='center'>
                          <TextField
                            size='small'
                            variant='standard'
                            value={getItemFacturacion(reg)}
                            onChange={(e) => handleItemFacturacionChange(reg.SrvD_Id, e.target.value)}
                            placeholder='Código'
                            inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem', padding: '2px 4px' } }}
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell align='center'>{reg.Pr_Nombre}</TableCell>
                        {showCantidad && (
                          <TableCell align='center'>{reg.SrvD_Cantidad ?? ''}</TableCell>
                        )}
                        <TableCell align='center'>
                          {moment(reg.Srv_FechaServicio).format('DD-MMM-YY')}
                        </TableCell>
                        <TableCell align='center'>
                          <Typography color='error' fontWeight='bold' fontSize='0.8rem'>
                            {reg.Srv_Factura}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          {Prm_Fact_Modificar === 1 && (
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => handleOpenDeleteConfirm(reg.SrvD_Id)}
                              title='Quitar de facturación'
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {/* Totals row */}
                <TableRow sx={{ backgroundColor: 'rgba(14,159,143,0.08)' }}>
                  <TableCell colSpan={4} align='right' sx={{ fontWeight: 'bold' }}>
                    TOTALES
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totales.subtotal)}
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totales.iva)}
                  </TableCell>
                  <TableCell align='center' sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totales.total)}
                  </TableCell>
                  <TableCell colSpan={showCantidad ? 6 : 5} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      <ConfirmDelete
        open={openConfirmDelete}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        message='¿Estás seguro de que deseas quitar este registro de la facturación?'
      />

      {/* Dialog para desmarcar facturados */}
      <Dialog open={openDesmarcarDialog} onClose={() => setOpenDesmarcarDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Desmarcar Servicios Facturados</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Ingrese los IDs de los servicios que desea desmarcar, separados por coma.
          </Typography>
          <TextField
            label='IDs de Servicio'
            value={desmarcarIdsText}
            onChange={(e) => setDesmarcarIdsText(e.target.value)}
            fullWidth
            size='small'
            sx={{ mt: 1 }}
            placeholder='Ej: 11305, 11306, 11375'
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDesmarcarDialog(false)} color='error' variant='outlined' size='small'>
            Cancelar
          </Button>
          <Button onClick={handleDesmarcarFacturados} variant='contained' color='warning' size='small' disabled={loadingDesmarcar}>
            {loadingDesmarcar ? <CircularProgress size={20} /> : 'Confirmar Desmarcado'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Facturacion;
